import React, {
  ReactNode,
  useImperativeHandle,
  useRef,
  useEffect,
  useState,
} from 'react';
import styled from 'styled-components';
import { FloatingPanel } from 'antd-mobile';
import { FloatingPanelRef } from 'antd-mobile/es/components/floating-panel';
import { minBy } from 'lodash';
import { usePrevious } from 'hooks';

export type EnhancedFloatingPanelRef = FloatingPanelRef & {
  focusFloatingPanel: (
    elementToFocus: React.MutableRefObject<HTMLElement>,
  ) => void;
  blurFloatingPanel: () => void;
};

type EnhancedFloatingPanelProps = {
  anchors: number[];
  children: ReactNode;
  handleDraggingOfContent: boolean;
  topMargin: number;
};

const ANIMATION_DURATION = 250; // 250ms is also the duration for ios keyboard animation

/**
 * Think about following cases when modifying this component
 * 1. anchors changed when the panel is still
 * 2. anchors changed while user is still dragging the panel
 * 3. anchors changed while the panel is still animating
 */
const EnhancedFloatingPanel = React.forwardRef<
  EnhancedFloatingPanelRef,
  EnhancedFloatingPanelProps
>(({ anchors, children, handleDraggingOfContent, topMargin = 0 }, ref) => {
  const floatingPanelRef = useRef<FloatingPanelRef>();
  const currentHeightRef = useRef<number>(anchors[0]);
  const currentHeightIndexRef = useRef<number>(0);
  const dummyInputRef = useRef<HTMLInputElement>();

  const [isPanelFocused, setIsPanelFocused] = useState<boolean>(false);
  const [isMoving, setIsMoving] = useState<boolean>(false);
  const [isAnimating, setIsAnimating] = useState<boolean>(false);

  useImperativeHandle(ref, () => ({
    focusFloatingPanel: elementToFocus => {
      setIsPanelFocused(true);
      dummyInputRef.current.focus();
      $(
        '.adm-floating-panel',
      )[0].style.transition = `transform ${ANIMATION_DURATION}ms`;
      setTimeout(() => {
        elementToFocus.current.focus();
      }, ANIMATION_DURATION);
    },
    setHeight: floatingPanelRef.current.setHeight,
    blurFloatingPanel: () => {
      setIsPanelFocused(false);
      setTimeout(() => {
        $('.adm-floating-panel')[0].style.transition = null;
      }, ANIMATION_DURATION);
    },
  }));

  const prevAnchors = usePrevious(anchors);
  useEffect(() => {
    if (isMoving || isAnimating) return;
    if (anchors === prevAnchors)
      currentHeightIndexRef.current = anchors.indexOf(
        minBy(anchors, n => Math.abs(n - currentHeightRef.current)),
      );
    const correctAnchor = anchors[currentHeightIndexRef.current];
    floatingPanelRef.current.setHeight(correctAnchor, { immediate: true });
  }, [anchors, isAnimating, isMoving]);

  return (
    <span
      onTouchStart={() => setIsMoving(true)}
      onTouchEnd={() => setIsMoving(false)}
      onTouchMove={e => {
        if (!isPanelFocused) return;
        e.preventDefault();
        e.stopPropagation();
      }}
    >
      <StyledDummyInput ref={dummyInputRef} />
      <StyledFloatingPanel
        ref={floatingPanelRef}
        anchors={anchors}
        // Disable floating panel from being dragged while a tour item is being sorted
        handleDraggingOfContent={handleDraggingOfContent}
        onHeightChange={(height, animating) => {
          currentHeightRef.current = height;
          setIsAnimating(animating);
        }}
        $isFocused={isPanelFocused}
        $topMargin={topMargin}
      >
        {children}
      </StyledFloatingPanel>
    </span>
  );
});

export default EnhancedFloatingPanel;

// trick mobile browser into thinking that the input does not conflict with the keyboard
const StyledDummyInput = styled.input`
  position: absolute;
  top: -10000px;
`;

const StyledFloatingPanel = styled(FloatingPanel)<{
  $isFocused?: boolean;
  $topMargin: number;
}>`
  ${({ $isFocused, $topMargin }) =>
    $isFocused &&
    `top: ${$topMargin} !important; transform: translateY(0) !important;`}

  box-shadow: 0px -3px 6px -4px rgba(0, 0, 0, 0.12),
    0px -6px 16px rgba(0, 0, 0, 0.08), 0px -9px 28px 8px rgba(0, 0, 0, 0.05);
  border-radius: 16px 16px 0px 0px;

  && {
    .adm-floating-panel-bar {
      width: 48px;
    }
  }
  && {
    .adm-floating-panel-content {
      ${({ $isFocused }) =>
        !$isFocused ? 'overflow-y: scroll' : 'overflow-y: hidden'}
    }
  }
`;
